import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Zap, User, Clock, ShieldCheck } from "lucide-react";
import { useFetch } from "../lib/api";
import { SectionLabel } from "../components/shared";

export function Automations() {
  const { data: workflows } = useFetch("/workflows");
  return (
    <main data-testid="automations-page">
      <section className="bg-[#F3EEE4] border-b border-[#E7E0D2]">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-14">
          <SectionLabel>Workflow Automations · built for local government</SectionLabel>
          <h1 className="font-display font-bold text-[38px] lg:text-[56px] tracking-tight leading-[1.03] max-w-4xl">Frameworks that <span className="text-[#3F4CE0]">run themselves.</span></h1>
          <p className="mt-4 text-[16px] text-[#1A1914]/65 max-w-2xl">Eight framework-based automations for cities, counties and local authorities — grant compliance, incident clocks, audits and disaster recovery — designed for two-person teams with zero slack.</p>
        </div>
      </section>
      <section className="max-w-[1320px] mx-auto px-5 lg:px-8 py-14">
        <div className="grid md:grid-cols-2 gap-4" data-testid="automation-grid">
          {(workflows || []).map((w) => (
            <Link key={w.slug} to={`/automations/${w.slug}`} data-testid={`automation-card-${w.slug}`} className="rp-card bg-white border border-[#E7E0D2] rounded-2xl p-6 group flex flex-col">
              <div className="flex items-center justify-between">
                <span className="font-mono-label text-[10px] px-2.5 py-1 rounded-full bg-[#3F4CE0]/5 border border-[#3F4CE0]/20 text-[#3F4CE0]">{w.framework}</span>
                <span className="inline-flex items-center gap-1 font-mono-label text-[10px] text-[#0E9355]"><Zap size={11} /> {w.automationLevel}% automated</span>
              </div>
              <h3 className="mt-4 font-display font-semibold text-[22px] group-hover:text-[#3F4CE0] transition-colors">{w.name}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[#1A1914]/60 line-clamp-2">{w.description}</p>
              <div className="mt-3 h-1.5 rounded-full bg-[#F3EEE4] overflow-hidden">
                <div className="h-full bg-[#12B76A] rounded-full transition-all duration-700" style={{ width: `${w.automationLevel}%` }} />
              </div>
              <div className="mt-4 pt-4 border-t border-[#E7E0D2] flex items-center justify-between text-[12.5px] text-[#1A1914]/55 mt-auto">
                <span className="inline-flex items-center gap-1.5"><User size={12} /> {w.persona}</span>
                <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {w.duration}</span>
                <ArrowRight size={15} className="text-[#3F4CE0] group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}

export function AutomationDetail() {
  const { slug } = useParams();
  const { data: workflows, loading } = useFetch("/workflows");
  const w = (workflows || []).find((x) => x.slug === slug);
  if (loading) return <main className="min-h-[50vh] flex items-center justify-center"><div className="w-9 h-9 rounded-full border-[3px] border-[#3F4CE0] border-t-transparent animate-spin" /></main>;
  if (!w) return <main className="max-w-3xl mx-auto px-5 py-24 text-center"><p>Automation not found.</p><Link to="/automations" className="text-[#3F4CE0] underline">Back</Link></main>;
  return (
    <main className="max-w-[1100px] mx-auto px-5 lg:px-8 py-12" data-testid="automation-detail-page">
      <Link to="/automations" className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-[#1A1914]/60 hover:text-[#3F4CE0] rp-btn"><ArrowLeft size={14} /> Automations</Link>
      <div className="mt-6 flex flex-wrap gap-2">
        <span className="font-mono-label text-[10px] px-2.5 py-1 rounded-full bg-[#3F4CE0]/5 border border-[#3F4CE0]/20 text-[#3F4CE0]">{w.framework}</span>
        <span className="font-mono-label text-[10px] px-2.5 py-1 rounded-full bg-[#12B76A]/10 border border-[#12B76A]/25 text-[#0E9355]">{w.automationLevel}% automated</span>
        <span className="font-mono-label text-[10px] px-2.5 py-1 rounded-full bg-[#F3EEE4] border border-[#E7E0D2] text-[#1A1914]/55">{w.duration}</span>
      </div>
      <h1 className="mt-4 font-display font-bold text-[36px] lg:text-[50px] tracking-tight leading-[1.05]">{w.name}</h1>
      <p className="mt-3 text-[16px] text-[#1A1914]/60 max-w-3xl">{w.description}</p>
      <div className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E5484D]/5 border border-[#E5484D]/20 text-[13.5px]">
        <ShieldCheck size={15} className="text-[#DC2626]" /> <strong>Risk addressed:</strong> {w.riskAddressed}
      </div>
      <div className="mt-10">
        <SectionLabel color="#12B76A">The workflow — step by step</SectionLabel>
        <div className="space-y-0" data-testid="automation-steps">
          {w.steps.map((s, i) => (
            <div key={s.n} className="relative flex gap-5 pb-7 last:pb-0">
              <div className="flex flex-col items-center">
                <span className="w-10 h-10 shrink-0 rounded-full bg-[#3F4CE0] text-white font-display font-bold text-[15px] flex items-center justify-center">{s.n}</span>
                {i < w.steps.length - 1 && <span className="flex-1 w-px bg-[#E7E0D2] mt-2" />}
              </div>
              <div className="bg-white border border-[#E7E0D2] rounded-2xl p-5 flex-1 rp-card">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-[16.5px]">{s.title}</h3>
                  <span className="font-mono-label text-[10px] text-[#1A1914]/45 shrink-0">{s.days === 0 ? "Continuous" : `~${s.days} days`}</span>
                </div>
                <p className="mt-1.5 text-[13.5px] text-[#1A1914]/60 flex items-start gap-1.5"><Zap size={13} className="text-[#0E9355] shrink-0 mt-0.5" /> {s.auto}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-10 bg-[#14130F] text-white rounded-2xl p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
        <div>
          <h3 className="font-display font-semibold text-[24px]">Deploy this workflow for your {w.persona.split("/")[0].trim().toLowerCase()}.</h3>
          <p className="mt-2 text-[14px] text-white/60">Free during early access for government entities. Setup call included.</p>
        </div>
        <Link to="/signup" data-testid="automation-deploy-cta" className="shrink-0 inline-flex items-center gap-2 px-6 py-3.5 text-[15px] font-semibold text-[#14130F] bg-white hover:bg-[#F3EEE4] rounded-xl rp-btn">Request access <ArrowRight size={15} /></Link>
      </div>
    </main>
  );
}
